import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { API, photoUrl } from '../lib/api';

const EditVehicle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [societes, setSocietes] = useState([]);
  const [showNewSociete, setShowNewSociete] = useState(false);
  const [newSocieteName, setNewSocieteName] = useState('');
  const [photos, setPhotos] = useState([]); // array of { path, url }
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    immatriculation: '',
    marque: '',
    modele: '',
    annee: '',
    type_vehicule: 'voiture',
    prix_achat: '',
    prix_vente: '',
    tva_applicable: false,
    societe_id: '',
    statut: 'en_stock',
    couleur: '',
    kilometrage: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [vehicleRes, societesRes] = await Promise.all([
        axios.get(`${API}/vehicles/${id}`),
        axios.get(`${API}/societes`)
      ]);
      
      const vehicle = vehicleRes.data;
      setFormData({
        immatriculation: vehicle.immatriculation,
        marque: vehicle.marque,
        modele: vehicle.modele,
        annee: vehicle.annee,
        type_vehicule: vehicle.type_vehicule || 'voiture',
        prix_achat: vehicle.prix_achat,
        prix_vente: vehicle.prix_vente || '',
        tva_applicable: vehicle.tva_applicable || false,
        societe_id: vehicle.societe_id,
        statut: vehicle.statut,
        couleur: vehicle.couleur || '',
        kilometrage: vehicle.kilometrage || '',
        description: vehicle.description || ''
      });
      // Photos: paths returned in photo_paths, URLs in photos (aligned by index)
      const paths = vehicle.photo_paths || vehicle.photos || [];
      const urls = vehicle.photos || [];
      setPhotos(paths.map((p, i) => ({ path: p, url: photoUrl(urls[i] || p) })));
      setSocietes(societesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoadingData(false);
    }
  };

  const handleAddSociete = async () => {
    if (!newSocieteName.trim()) {
      toast.error('Veuillez entrer un nom de société');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/societes`, { nom: newSocieteName });
      setSocietes([...societes, res.data]);
      setFormData({ ...formData, societe_id: res.data.id });
      setNewSocieteName('');
      setShowNewSociete(false);
      toast.success('Société ajoutée avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout de la société');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';

    const compressionOptions = {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 1600,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.85,
    };

    setUploading(true);
    for (const file of files) {
      try {
        const compressed = await imageCompression(file, compressionOptions);
        const jpegFile = new File([compressed], (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
        // Preview immédiat via blob local (le path public sera actif après la sauvegarde)
        const previewUrl = URL.createObjectURL(jpegFile);
        const form = new FormData();
        form.append('file', jpegFile);
        const { data } = await axios.post(`${API}/upload/photo`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPhotos(prev => [...prev, { path: data.path, url: previewUrl }]);
      } catch (error) {
        console.error('Photo upload failed:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload de la photo');
      }
    }
    setUploading(false);
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.immatriculation || !formData.marque || !formData.modele || !formData.prix_achat || !formData.societe_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        immatriculation: formData.immatriculation,
        marque: formData.marque,
        modele: formData.modele,
        annee: parseInt(formData.annee),
        type_vehicule: formData.type_vehicule,
        prix_achat: parseFloat(formData.prix_achat),
        prix_vente: formData.prix_vente ? parseFloat(formData.prix_vente) : null,
        tva_applicable: !!formData.tva_applicable,
        societe_id: formData.societe_id,
        statut: formData.statut,
        couleur: formData.couleur || null,
        kilometrage: formData.kilometrage ? parseInt(formData.kilometrage) : null,
        description: formData.description || null,
        photos: photos.map(p => p.path)
      };
      
      await axios.put(`${API}/vehicles/${id}`, payload);
      toast.success('Véhicule modifié avec succès');
      navigate(`/vehicles/${id}`);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification du véhicule');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  return (
    <div data-testid="edit-vehicle-page">
      <div className="mb-6">
        <Link to={`/vehicles/${id}`} data-testid="back-btn" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft size={20} />
          <span>Retour au véhicule</span>
        </Link>
      </div>

      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}}>Modifier le véhicule</h2>
        <p className="text-slate-600 mt-2">Mettez à jour les informations du véhicule</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
          <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Informations générales</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Immatriculation *</label>
              <input
                type="text"
                name="immatriculation"
                data-testid="immatriculation-input"
                value={formData.immatriculation}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Marque *</label>
              <input
                type="text"
                name="marque"
                data-testid="marque-input"
                value={formData.marque}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Modèle *</label>
              <input
                type="text"
                name="modele"
                data-testid="modele-input"
                value={formData.modele}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Année *</label>
              <input
                type="number"
                name="annee"
                data-testid="annee-input"
                value={formData.annee}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono"
                min="1900"
                max={new Date().getFullYear() + 1}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Type de véhicule *</label>
              <select
                name="type_vehicule"
                data-testid="type-vehicule-select"
                value={formData.type_vehicule}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                required
              >
                <option value="voiture">Voiture</option>
                <option value="utilitaire">Utilitaire</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Couleur</label>
              <input
                type="text"
                name="couleur"
                data-testid="couleur-input"
                value={formData.couleur}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Kilométrage (km)</label>
              <input
                type="number"
                name="kilometrage"
                data-testid="kilometrage-input"
                value={formData.kilometrage}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea
              name="description"
              data-testid="description-input"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
          <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Informations financières</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Prix d'achat (€) *</label>
              <input
                type="number"
                name="prix_achat"
                data-testid="prix-achat-input"
                value={formData.prix_achat}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono"
                step="0.01"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Prix de vente TTC (€)</label>
              <input
                type="number"
                name="prix_vente"
                data-testid="prix-vente-input"
                value={formData.prix_vente}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono"
                step="0.01"
              />
            </div>
          </div>

          <div className="mt-4 flex items-start gap-3 p-3 bg-slate-50 rounded-md border border-slate-200">
            <input
              type="checkbox"
              id="tva_applicable"
              name="tva_applicable"
              data-testid="tva-applicable-checkbox"
              checked={formData.tva_applicable}
              onChange={handleChange}
              className="mt-1 h-4 w-4 text-slate-900 rounded focus:ring-slate-900"
            />
            <label htmlFor="tva_applicable" className="flex-1 cursor-pointer">
              <span className="block text-sm font-medium text-slate-900">TVA 20% applicable</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Cocher si le véhicule est soumis à la TVA. Le HT sera calculé et affiché automatiquement.
              </span>
              {formData.tva_applicable && formData.prix_vente && (
                <span data-testid="prix-ht-preview" className="block text-xs font-semibold text-green-700 mt-1 font-mono">
                  → Prix HT : {Math.round(parseFloat(formData.prix_vente) / 1.20).toLocaleString('fr-FR')} €
                </span>
              )}
            </label>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
          <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Achat et statut</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Société d'achat *</label>
              {!showNewSociete ? (
                <div className="flex gap-2">
                  <select
                    name="societe_id"
                    data-testid="societe-select"
                    value={formData.societe_id}
                    onChange={handleChange}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                    required
                  >
                    <option value="">Sélectionnez une société</option>
                    {societes.map(societe => (
                      <option key={societe.id} value={societe.id}>{societe.nom}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    data-testid="add-societe-btn"
                    onClick={() => setShowNewSociete(true)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors whitespace-nowrap"
                  >
                    + Nouvelle
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    data-testid="new-societe-input"
                    value={newSocieteName}
                    onChange={(e) => setNewSocieteName(e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="Nom de la société"
                  />
                  <button
                    type="button"
                    data-testid="save-societe-btn"
                    onClick={handleAddSociete}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    Ajouter
                  </button>
                  <button
                    type="button"
                    data-testid="cancel-societe-btn"
                    onClick={() => {
                      setShowNewSociete(false);
                      setNewSocieteName('');
                    }}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Statut *</label>
              <select
                name="statut"
                data-testid="statut-select"
                value={formData.statut}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                required
              >
                <option value="en_stock">En stock</option>
                <option value="vendu">Vendu</option>
                <option value="reserve">Réservé</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
          <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Photos</h3>
          
          <div className="mb-2">
            <p className="text-sm text-slate-600 mb-4">
              Les images sont automatiquement compressées pour optimiser le stockage. 
              Recommandé : 5-10 photos maximum par véhicule.
            </p>
          </div>

          <div className="mb-4">
            <label className="block w-full cursor-pointer">
              <div data-testid="upload-area" className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors">
                <Upload className="mx-auto text-slate-400 mb-3" size={32} />
                <p className="text-slate-600 mb-2">Cliquez pour ajouter des photos</p>
                <p className="text-slate-400 text-sm">Formats acceptés : JPG, PNG, WEBP</p>
              </div>
              <input
                type="file"
                data-testid="photo-input"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>

          {photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <div key={photo.path} data-testid={`photo-preview-${index}`} className="relative group">
                  <img src={photo.url} alt={`Preview ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                  <button
                    type="button"
                    data-testid={`remove-photo-${index}`}
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X size={16} />
                  </button>
                  {index === 0 && (
                    <span className="absolute bottom-2 left-2 bg-slate-900 text-white text-xs px-2 py-1 rounded">Principale</span>
                  )}
                </div>
              ))}
              {uploading && (
                <div data-testid="photo-uploading" className="flex flex-col items-center justify-center h-32 rounded-lg bg-slate-50 border border-dashed border-slate-300 text-slate-500 text-sm gap-2">
                  <Loader2 className="animate-spin" size={20} />
                  <span>Envoi...</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            data-testid="submit-btn"
            disabled={loading}
            className="px-6 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-colors shadow-sm disabled:opacity-50"
          >
            {loading ? 'Modification en cours...' : 'Enregistrer les modifications'}
          </button>
          <Link
            to={`/vehicles/${id}`}
            data-testid="cancel-btn"
            className="px-6 py-3 bg-white text-slate-900 border border-slate-200 rounded-md hover:bg-slate-50 font-medium transition-colors"
          >
            Annuler
          </Link>
        </div>
      </form>
    </div>
  );
};

export default EditVehicle;
